import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { CheckCircle2Icon, ClockIcon, FileTextIcon, XCircleIcon, Loader2Icon } from 'lucide-react'

import { apiClient } from '@/api/client'
import { useAuth } from '@/contexts/AuthContext'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'

export interface QoEFinancialStatement {
  fs_id: string
  deal_id: string
  firm_id: string | null
  period_label: string
  period_end_date: string
  fs_type: string
  version: number
  source_filename: string | null
  blob_url: string | null
  parsed_data?: Record<string, unknown> | null
  status: 'draft' | 'pending_review' | 'approved' | 'superseded'
  approved_by: string | null
  approved_at: string | null
  rejection_reason: string | null
  created_by: string | null
  created_at: string
}

interface Props {
  fs: QoEFinancialStatement
  onStatusChange?: () => void
}

const STATUS_CONFIG: Record<
  QoEFinancialStatement['status'],
  { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline'; icon: typeof FileTextIcon }
> = {
  draft: { label: 'Draft', variant: 'secondary', icon: FileTextIcon },
  pending_review: { label: 'Pending Review', variant: 'outline', icon: ClockIcon },
  approved: { label: 'Approved', variant: 'default', icon: CheckCircle2Icon },
  superseded: { label: 'Superseded', variant: 'secondary', icon: XCircleIcon },
}

export function QoEFinancialStatementCard({ fs, onStatusChange }: Props) {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const role = (user as { role?: string } | null)?.role ?? ''
  const canApproveReject = role === 'cpa' || role === 'super_admin'

  const cfg = STATUS_CONFIG[fs.status] ?? STATUS_CONFIG.draft
  const StatusIcon = cfg.icon

  async function submitForReview() {
    setLoading(true)
    try {
      await apiClient.post(`/v1/qoe/financial-statements/${fs.fs_id}/submit-for-review`)
      toast.success('Submitted for CPA review')
      onStatusChange?.()
    } catch {
      toast.error('Failed to submit for review')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="rounded-lg border bg-card p-4 flex flex-col gap-3">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="flex items-center gap-2">
            <span className="font-medium text-sm text-brand-text">
              {fs.period_label}
            </span>
            <span className="text-xs text-muted-foreground">v{fs.version}</span>
          </div>
          {fs.source_filename && (
            <p className="text-xs text-muted-foreground mt-0.5">{fs.source_filename}</p>
          )}
        </div>
        <Badge variant={cfg.variant} className="flex items-center gap-1 shrink-0">
          <StatusIcon className="h-3 w-3" />
          {cfg.label}
        </Badge>
      </div>

      {fs.rejection_reason && (
        <p className="text-xs text-destructive bg-destructive/10 rounded p-2">
          <span className="font-medium">Rejected: </span>{fs.rejection_reason}
        </p>
      )}

      {fs.approved_by && (
        <p className="text-xs text-emerald-600">
          Approved by {fs.approved_by}
          {fs.approved_at ? ` on ${new Date(fs.approved_at).toLocaleDateString()}` : ''}
        </p>
      )}

      <div className="flex gap-2 flex-wrap">
        {fs.status === 'draft' && (
          <Button size="sm" variant="outline" className="text-xs" onClick={submitForReview} disabled={loading}>
            {loading && <Loader2Icon className="h-3 w-3 animate-spin mr-1" />}
            Submit for Review
          </Button>
        )}
        {fs.status === 'pending_review' && canApproveReject && (
          <Button
            size="sm"
            variant="default"
            className="text-xs"
            onClick={() => navigate(`/qoe/financial-statements/${fs.fs_id}/review`)}
          >
            Review
          </Button>
        )}
      </div>
    </div>
  )
}
